/**
 * Cart Engraving Manager
 * Automatically adds/updates Custom Engraving product based on items with engraving property
 * Version: 3.0 - Fixed navigation issue by only syncing on cart drawer open
 * Timestamp: 2026-02-02T03:30:00Z - Cache buster
 */

console.log('🔧 cart-engraving-manager.js v3.0 FILE LOADED');

const ENGRAVING_VARIANT_ID_10 = '47803104264419';
const ENGRAVING_VARIANT_ID_5 = '47904269861091';
const ENGRAVING_PROPERTY_NAME = '_Engraving';

class CartEngravingManager extends HTMLElement {
  constructor() {
    super();
    this.isUpdating = false;
    this.lastSyncTime = 0;
    this.cartDrawerOpen = false;
    this.init();
  }

  init() {
    console.log('🚀 Engraving Manager v3.0 Initialized');
    
    // Check what page we're on
    const isCartPage = document.body.classList.contains('template-cart');
    
    console.log('✅ Engraving manager active on', document.body.className);
    
    // ONLY sync when cart drawer is open - this prevents navigation issues
    // The cart drawer being open is a stable state where we can safely make API calls
    
    // Listen for cart drawer opening via MutationObserver
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer) {
      console.log('👀 Watching cart drawer for opens');
      const observer = new MutationObserver(() => {
        const dialog = cartDrawer.querySelector('[open]');
        if (dialog && !this.cartDrawerOpen) {
          this.cartDrawerOpen = true;
          console.log('🛒 Cart drawer opened - checking engraving');
          // Give cart drawer time to fully render
          setTimeout(() => this.syncEngravingProduct(), 500);
        } else if (!dialog && this.cartDrawerOpen) {
          this.cartDrawerOpen = false;
          console.log('🚪 Cart drawer closed');
        }
      });
      observer.observe(cartDrawer, { attributes: true, subtree: true, attributeFilter: ['open'] });
      
      // Also listen for cart updates ONLY when drawer is already open
      document.documentElement.addEventListener('cart:update', () => {
        if (this.cartDrawerOpen) {
          console.log('📡 cart:update event detected (drawer open)');
          // Longer delay to let Shopify finish its work
          setTimeout(() => this.syncEngravingProduct(), 800);
        } else {
          console.log('📡 cart:update event detected (drawer closed - skipping sync)');
        }
      });
    } else {
      console.log('⚠️ cart-drawer-component not found');
    }
    
    // For dedicated cart page, sync after page load
    if (isCartPage) {
      setTimeout(() => {
        console.log('⏰ Initial cart page check');
        this.syncEngravingProduct();
      }, 1000);
    }
  }

  async syncEngravingProduct() {
    if (this.isUpdating) {
      console.log('⏳ Already updating, skipping');
      return;
    }
    
    // Rate limit to prevent rapid calls - minimum 1.5 seconds between syncs
    const now = Date.now();
    if (now - this.lastSyncTime < 1500) {
      console.log('⏱️ Rate limiting, too soon since last sync');
      return;
    }
    this.lastSyncTime = now;
    
    this.isUpdating = true;
    
    try {
      const cart = await this.getCart();
      const engravingCounts = this.countEngravingItems(cart);
      const currentQty5 = this.getCurrentEngravingQuantity(cart, ENGRAVING_VARIANT_ID_5);
      const currentQty10 = this.getCurrentEngravingQuantity(cart, ENGRAVING_VARIANT_ID_10);
      
      console.log('🔍 Engraving Check:', { 
        engravingCounts,
        currentQty5,
        currentQty10,
        totalItems: cart.items.length 
      });

      const { qty5, qty10 } = engravingCounts;

      const needsUpdate5 = qty5 !== currentQty5;
      const needsUpdate10 = qty10 !== currentQty10;

      if (needsUpdate5 || needsUpdate10) {
        // Process updates one at a time with delay between them
        if (needsUpdate5) {
          if (qty5 > 0) {
            console.log('✅ Adding/updating $5 engraving product');
            await this.updateEngravingQuantity(cart, ENGRAVING_VARIANT_ID_5, qty5);
            // Small delay between operations
            await new Promise(resolve => setTimeout(resolve, 300));
          } else if (currentQty5 > 0) {
            console.log('🗑️ Removing $5 engraving product');
            await this.removeEngravingProduct(cart, ENGRAVING_VARIANT_ID_5);
            await new Promise(resolve => setTimeout(resolve, 300));
          }
        }

        if (needsUpdate10) {
          if (qty10 > 0) {
            console.log('✅ Adding/updating $10 engraving product');
            await this.updateEngravingQuantity(cart, ENGRAVING_VARIANT_ID_10, qty10);
          } else if (currentQty10 > 0) {
            console.log('🗑️ Removing $10 engraving product');
            await this.removeEngravingProduct(cart, ENGRAVING_VARIANT_ID_10);
          }
        }
        
        console.log('✅ Engraving sync complete');
      } else {
        console.log('⏭️ No change needed');
      }
    } catch (error) {
      // Only log if it's a real error, not a navigation abort
      if (error.name !== 'AbortError' && !error.message.includes('Failed to fetch')) {
        console.error('❌ Error syncing engraving:', error);
      } else {
        console.log('⚠️ Sync interrupted (page navigating)');
      }
    } finally {
      this.isUpdating = false;
    }
  }

  async getCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) {
        if (response.status === 429) {
          console.warn('⏱️ Rate limited, backing off');
          this.lastSyncTime = Date.now() + 5000; // Back off for 5 seconds
          throw new Error('Rate limited');
        }
        throw new Error(`Cart fetch failed: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('❌ Failed to fetch cart:', error);
      throw error;
    }
  }

  countEngravingItems(cart) {
    let totalQty5 = 0;
    let totalQty10 = 0;
    
    cart.items.forEach(item => {
      // Skip the engraving product itself
      if (
        item.variant_id === parseInt(ENGRAVING_VARIANT_ID_10) ||
        item.variant_id === parseInt(ENGRAVING_VARIANT_ID_5)
      ) return;
      
      // Check if item has engraving property
      const hasEngraving = item.properties && (
        item.properties[ENGRAVING_PROPERTY_NAME] || 
        item.properties['Engraving'] ||
        item.properties['_Engraving']
      );
      
      if (hasEngraving) {
        // Check if customer specified engraving quantities (for sets)
        const qty5 = item.properties['Engraving Quantity $5'];
        const qty10 = item.properties['Engraving Quantity $10'];
        const specifiedQty = item.properties['Engraving Quantity'];

        if (qty5 || qty10) {
          const q5 = parseInt(qty5) || 0;
          const q10 = parseInt(qty10) || 0;
          totalQty5 += q5 * item.quantity;
          totalQty10 += q10 * item.quantity;
          console.log(`📦 ${item.product_title}: $5 x ${q5}, $10 x ${q10} × ${item.quantity} items`);
        } else if (specifiedQty) {
          // Backward compatibility
          const qty = parseInt(specifiedQty) || 1;
          totalQty10 += qty * item.quantity;
          console.log(`📦 ${item.product_title}: ${qty} pieces × ${item.quantity} items (default to $10)`);
        } else {
          // Default: 1 engraving per cart item
          totalQty10 += item.quantity;
          console.log(`📦 ${item.product_title}: 1 per item × ${item.quantity} (default to $10)`);
        }
      }
    });

    console.log(`Total engraving quantities needed: $5=${totalQty5}, $10=${totalQty10}`);
    return { qty5: totalQty5, qty10: totalQty10 };
  }

  getCurrentEngravingQuantity(cart, variantId) {
    const engravingItem = cart.items.find(item => 
      item.variant_id === parseInt(variantId)
    );
    return engravingItem ? engravingItem.quantity : 0;
  }

  async updateEngravingQuantity(cart, variantId, newQuantity) {
    const engravingItem = cart.items.find(item => 
      item.variant_id === parseInt(variantId)
    );

    if (engravingItem) {
      // Update existing quantity using cart/change.js with JSON
      console.log(`📝 Updating variant ${variantId} to quantity ${newQuantity} using key ${engravingItem.key}`);
      
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: engravingItem.key,
          quantity: newQuantity
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Failed to update engraving quantity: ${response.status}`, errorText);
        throw new Error(`Cart change failed: ${response.status}`);
      }
      
      // Consume response to properly close connection
      await response.json();
      console.log(`✅ Successfully updated variant ${variantId}`);
    } else {
      // Add new engraving product using JSON format for Shopify cart API
      console.log(`➕ Adding new variant ${variantId} with quantity ${newQuantity}`);
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: parseInt(variantId),
            quantity: parseInt(newQuantity)
          }]
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Failed to add engraving product: ${response.status}`, errorText);
        throw new Error(`Cart add failed: ${response.status}`);
      }
      
      // Consume response to properly close connection
      await response.json();
      console.log(`✅ Successfully added variant ${variantId}`);
    }
  }

  async removeEngravingProduct(cart, variantId) {
    const engravingItem = cart.items.find(item => 
      item.variant_id === parseInt(variantId)
    );

    if (engravingItem) {
      console.log(`🗑️ Removing variant ${variantId} using key ${engravingItem.key}`);
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: engravingItem.key,
          quantity: 0
        })
      });

      if (!response.ok) {
        console.error(`❌ Failed to remove engraving: ${response.status}`);
      } else {
        await response.json();
        console.log(`✅ Successfully removed variant ${variantId}`);
      }
    }
  }
}

if (!customElements.get('cart-engraving-manager')) {
  console.log('✨ Defining cart-engraving-manager custom element');
  customElements.define('cart-engraving-manager', CartEngravingManager);
} else {
  console.log('⚠️ cart-engraving-manager already defined');
}
